import React, { useState } from "react";
import { CheckIcon, Edit2Icon } from "lucide-react";
import { Deliverable, Workstream } from "tekkr-common/dist/model/project/project";
import { TaskList } from "./components/task-list";
import { useProject } from "../../data/project";
import { cn } from "../../lib/utils";
import { DeliverableCard } from "../../components/shared/project/deliverable-card";
import { Button } from "../../components/ui/button";
import Spinner from "../../components/ui/spinner";
import { useProjectProgressQuery } from "../../data/queries/project-progress";
import { useProjectTasksQuery } from "../../data/queries/project-tasks";
import Contenteditable from "../../components/shared/contenteditable";
import { PlannedTimeline } from "../../components/shared/project/planned-timeline";
import { useUpdateWorkstreamMutation } from "../../data/mutations/update-workstream";
import { OwnerSelector } from "../../components/shared/project/owner-selector";
import { LinkedDocumentField } from "./components/linked-document-field";
import { StatusCircle } from "../../components/shared/project/status-circle";
import { StatusCounts } from "../../data/project-progress";
import { formatWorkstreamLetter } from "../../lib/projects/utils";

export function ProjectWorkstreamSubpage({
    workstreamId,
    ...props
}: {
    workstreamId: string;
    onSelectDeliverable: (deliverableId: string) => void;
}) {
    const { getWorkstream, project } = useProject();
    const ws = getWorkstream(workstreamId);
    const wsIndex = project.outline.workstreams.findIndex((w) => w.id === workstreamId);

    const progress = useProjectProgressQuery();
    const tasksQuery = useProjectTasksQuery();

    if (!ws) {
        return <>loading...</>;
    }

    const tasks = tasksQuery.data?.data.filter((t) => t.workstreamId === ws.id && !t.deliverableId) ?? [];

    return (
        <div className={"flex flex-col gap-2 p-1"}>
            <Header
                ws={ws}
                wsIndex={wsIndex >= 0 ? wsIndex : undefined}
                statusCounts={progress.data?.getWorkstreamStatusCounts(ws.id)}
            />

            {!!tasks.length && <TaskList tasks={tasks} />}

            {ws.deliverables.length > 0 && (
                <>
                    <h4 className={"mt-4"}>Deliverables</h4>
                    <div className={"grid grid-cols-2 gap-3 lg:grid-cols-3"}>
                        {ws.deliverables.map((d, dIndex) => (
                            <DeliverableCard
                                onClick={() => props.onSelectDeliverable(d.id)}
                                key={d.id}
                                workstreamId={ws.id}
                                workstreamIndex={wsIndex >= 0 ? wsIndex : undefined}
                                deliverableIndex={dIndex}
                                deliverable={d}
                                statusCounts={progress.data?.getDeliverableStatusCounts(ws.id, d.id)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function Header({ ws, wsIndex, statusCounts }: { ws: Workstream; wsIndex?: number; statusCounts?: StatusCounts }) {
    const wsLetter = formatWorkstreamLetter(wsIndex);
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(ws.name);
    const [description, setDescription] = useState(ws.description);

    const updateMutation = useUpdateWorkstreamMutation(ws.id);

    function save() {
        updateMutation.mutate({
            name,
            description,
        });
        setIsEditing(false);
    }

    function cancel() {
        setName(ws.name);
        setDescription(ws.description);
        setIsEditing(false);
    }

    const timeline = getTimelineForDeliverables(ws.deliverables);

    return (
        <div className={"flex flex-row items-start gap-4"}>
            <div className={"flex flex-1 flex-col gap-2"}>
                <div>
                    <p className={"text-sm text-muted-foreground"}>Workstream</p>
                    <h2 className={cn("transition-all", isEditing && "-mx-1 rounded-md bg-input px-2 py-1")}>
                        <Contenteditable
                            value={name}
                            onChange={setName}
                            className={"outline-none"}
                            disabled={!isEditing}
                        />
                    </h2>
                </div>
                <PlannedTimeline
                    {...timeline}
                    infoTooltip={"This timeline is derived from the deliverables' timelines."}
                />
                <WorkstreamOwner ownerId={ws.ownerId ?? null} workstreamId={ws.id} />
                <p className={cn("transition-all", isEditing && "-mx-1 rounded-md bg-input px-2 py-1")}>
                    <Contenteditable
                        disabled={!isEditing}
                        className={"outline-none"}
                        value={description}
                        onChange={setDescription}
                    />
                </p>
                {!isEditing && !updateMutation.isPending && (
                    <Button
                        variant={"ghost"}
                        size={"sm"}
                        className={"-mt-1 self-start text-muted-foreground"}
                        onClick={() => setIsEditing(true)}
                    >
                        <Edit2Icon className={"me-2 h-4 w-4"} /> Edit
                    </Button>
                )}
                {updateMutation.isPending && (
                    <div
                        className={
                            "mb-1 flex flex-row items-center gap-2 p-1 text-sm font-medium text-muted-foreground"
                        }
                    >
                        <Spinner className={"h-5 w-5 self-start"} /> Saving...
                    </div>
                )}
                {isEditing && (
                    <div className={"-mx-1 mt-2 flex flex-row gap-2"}>
                        <Button onClick={save} size={"sm2"}>
                            <CheckIcon className={"me-1 h-5 w-5"} />
                            Done
                        </Button>
                        <Button onClick={cancel} size={"sm2"} variant={"ghost"}>
                            Cancel
                        </Button>
                    </div>
                )}
                <LinkedDocumentField
                    target={["workstream-file", ws.id]}
                    label="Workstream File:"
                    dialogTitle="Add Workstream File"
                />
            </div>
            {statusCounts && statusCounts.total > 0 && (
                <StatusCircle counts={statusCounts} size="lg">
                    {wsLetter && <span className="text-sm font-semibold text-muted-foreground">{wsLetter}</span>}
                </StatusCircle>
            )}
        </div>
    );
}

function WorkstreamOwner(props: { ownerId: string | null; workstreamId: string }) {
    const [ownerId, setOwnerId] = useState(props.ownerId);
    const updateWorkstreamMutation = useUpdateWorkstreamMutation(props.workstreamId);

    function handleOwnerChange(newOwnerId: string | null) {
        updateWorkstreamMutation.mutate({ ownerId: newOwnerId });
        setOwnerId(newOwnerId);
    }

    return <OwnerSelector ownerId={ownerId} onOwnerChange={handleOwnerChange} label={"Workstream owner:"} />;
}

export function getTimelineForDeliverables(deliverables: Deliverable[]): {
    from: string | undefined;
    to: string | undefined;
} {
    if (!deliverables.length) {
        return { from: undefined, to: undefined };
    }
    const from = deliverables.reduce(
        (min, d) => (!min || (d.startDate && d.startDate < min) ? d.targetDate : min),
        deliverables[0].startDate
    );
    const to = deliverables.reduce(
        (max, d) => (!max || (d.targetDate && d.targetDate > max) ? d.targetDate : max),
        deliverables[0].targetDate
    );
    return { from, to };
}
